import { useMemo } from "react";
import { useV2ListPhasesForAccountQuery } from "@generated";
import type { PhaseRow } from "../types/Phase";

export interface UsePhasesListResult {
  rows: PhaseRow[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export interface UsePhasesListArgs {
  archived: boolean;
}

export const usePhasesList = ({
  archived,
}: UsePhasesListArgs): UsePhasesListResult => {
  // v2 endpoints carry no cache tags, so mutations never invalidate this
  // list. Refetch on remount (e.g. returning from New/Edit) so saves show up.
  const { data, isLoading, isFetching, refetch } =
    useV2ListPhasesForAccountQuery(
      { filter: { active: !archived } },
      { refetchOnMountOrArgChange: true }
    );

  // Memoize the rows array so its reference stays stable across renders
  // when the underlying RTK Query data hasn't changed. Without this, every
  // re-render (e.g. while a move() mutation is in flight) produces a new
  // array reference, which invalidates AdminListTable's sortedData memo
  // and triggers its data->localRows sync effect — clobbering any
  // optimistic drag-drop update.
  const rows: PhaseRow[] = useMemo(
    () =>
      data?.data?.map((record: any) => ({
        id: String(record.id),
        position: record.attributes.position ?? 0,
        name: record.attributes.name ?? "",
        sowLanguage: record.attributes["sow-language"] ?? null,
        isDefault: !!record.attributes.default,
        deletedAt: record.attributes["deleted-at"] ?? null,
      })) ?? [],
    [data]
  );

  return {
    rows,
    totalCount: data?.meta?.["record-count"] ?? rows.length,
    isLoading,
    isFetching,
    refetch,
  };
};
